import { useState } from "react";
import { Button, Form, Input, InputNumber, Modal, Select, Table, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../../components/PageHeader";
import { useApi } from "../../helpers/useApi";
import { useUnitConversionsQuery, useUnitsQuery } from "../../helpers/queries";
import { hasPermission } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import type { Unit, UnitConversion } from "../../api-models";

// Units is a small reference list — /api/units returns a plain array, not the
// Paginated<T> envelope the generic ListPage expects, so this page talks to the
// TanStack Query hook directly (same pattern as StockLocationsPage).
export default function UnitsPage() {
  const { user } = useUser();
  const canWrite = hasPermission(user, "Items.Write");
  const { data: units = [], isLoading } = useUnitsQuery();
  const { data: conversions = [], isLoading: conversionsLoading } = useUnitConversionsQuery();
  const { request } = useApi();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Unit | null>(null);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [conversionOpen, setConversionOpen] = useState(false);
  const [conversionForm] = Form.useForm();

  function reload() {
    queryClient.invalidateQueries({ queryKey: ["units"] });
  }

  async function handleSubmit() {
    const values = await form.validateFields();
    if (editing) await request({ method: "PUT", url: `/units/${editing.id}`, data: values });
    else await request({ method: "POST", url: "/units", data: values });
    setOpen(false);
    reload();
  }

  async function handleCreateConversion() {
    const values = await conversionForm.validateFields();
    await request({ method: "POST", url: "/unit-conversions", data: values });
    setConversionOpen(false);
    conversionForm.resetFields();
    queryClient.invalidateQueries({ queryKey: ["unit-conversions"] });
  }

  return (
    <>
      <PageHeader
        title="Units"
        extra={
          canWrite && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null);
                form.resetFields();
                setOpen(true);
              }}
            >
              New unit
            </Button>
          )
        }
      />
      <Table<Unit>
        rowKey="id"
        loading={isLoading}
        dataSource={units}
        columns={[
          { title: "Name", dataIndex: "name" },
          { title: "Abbreviation", dataIndex: "abbreviation" },
          ...(canWrite
            ? [
                {
                  title: "",
                  key: "edit",
                  render: (_: unknown, r: Unit) => (
                    <Button
                      type="link"
                      onClick={() => {
                        setEditing(r);
                        form.setFieldsValue(r);
                        setOpen(true);
                      }}
                    >
                      Edit
                    </Button>
                  ),
                },
              ]
            : []),
        ]}
      />
      <Modal title={editing ? "Edit unit" : "New unit"} open={open} onCancel={() => setOpen(false)} onOk={handleSubmit} destroyOnHidden>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="abbreviation" label="Abbreviation" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <PageHeader
        title="Unit conversions"
        extra={
          canWrite && (
            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                conversionForm.resetFields();
                setConversionOpen(true);
              }}
            >
              New conversion
            </Button>
          )
        }
      />
      <Typography.Paragraph type="secondary">
        Explicit, approved factors between units — a request can never be made in a unit without one of these.
      </Typography.Paragraph>
      <Table<UnitConversion>
        rowKey="id"
        loading={conversionsLoading}
        dataSource={conversions}
        columns={[
          { title: "From", render: (_, r) => r.fromUnit?.abbreviation },
          { title: "To", render: (_, r) => r.toUnit?.abbreviation },
          { title: "Factor", dataIndex: "factor" },
        ]}
      />
      <Modal
        title="New unit conversion"
        open={conversionOpen}
        onCancel={() => setConversionOpen(false)}
        onOk={handleCreateConversion}
        destroyOnHidden
      >
        <Form form={conversionForm} layout="vertical">
          <Form.Item name="fromUnitId" label="From unit" rules={[{ required: true }]}>
            <Select options={units.map((u) => ({ value: u.id, label: `${u.name} (${u.abbreviation})` }))} />
          </Form.Item>
          <Form.Item name="toUnitId" label="To unit" rules={[{ required: true }]}>
            <Select options={units.map((u) => ({ value: u.id, label: `${u.name} (${u.abbreviation})` }))} />
          </Form.Item>
          <Form.Item name="factor" label="Factor (1 from-unit = factor × to-unit)" rules={[{ required: true }]}>
            <InputNumber min={0.000001} step={0.001} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
